import React, { useState } from 'react';
import { motion } from 'motion/react';
// import classNames from 'classnames';
import { 
  Code, 
  Users, 
  Briefcase, 
  Award, 
  Tag, 
  Activity,
  CheckCircle,
  Calendar,
  Phone,
  Mail,
  GraduationCap,
  BookOpen,
  Rocket,
  Target,
  ChevronDown
} from 'lucide-react';
import { courseData } from '../mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

import logo from "../assets/images/logo_h.png"

const iconMap = {
  code: Code,
  users: Users,
  briefcase: Briefcase,
  award: Award,
  tag: Tag,
  activity: Activity
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const LandingPage = () => {
  const whatsappLink = 'https://wa.me/919972614469';
  
  const handleEnrollClick = () => {
    window.open(whatsappLink, '_blank');
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img 
                src={logo} 
                alt="Inkube Technologies" 
                className="logo-image"
              />
            </div>
            <nav className="nav">
              <a href="#about" className="nav-link">About</a>
              <a href="#curriculum" className="nav-link">Curriculum</a>
              <a href="#mentorship" className="nav-link">Mentorship</a>
              <a href="#internship" className="nav-link">Internship</a>
            </nav>
            <button className="btn-primary" onClick={handleEnrollClick}>
              Enroll Now
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="gradient-hero">
          <div className="container">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 150 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.1 }}
            >
              <h1 className="hero-title">{courseData.title}</h1>
              <p className="hero-subtitle">{courseData.subtitle}</p>
              <div className="hero-cta">
                <button className="btn-primary btn-large" onClick={handleEnrollClick}>
                  Join Now for ₹{courseData.price}
                </button>
                <div className="hero-meta">
                  <Calendar size={20} />
                  <span>{courseData.duration} • Hands-on Training</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about-section">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">About the Program</h2>
            <p className="section-description">{courseData.about}</p>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section id="curriculum" className="section curriculum-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">Curriculum Overview</h2>
            <p className="section-subtitle">Comprehensive 12-week training program</p>
          </motion.div>

          <div className="curriculum-grid">
            {/* Python Track */}
            <motion.div
              className="curriculum-track"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.2 }}
            >
              <div className="track-header">
                <Code size={32} className="track-icon" />
                <h3 className="track-title">Python Programming</h3>
                <p className="track-duration">Weeks 1-6</p>
              </div>
              <div className="track-content">
                <Accordion type="single" collapsible className="week-accordion">
                  {courseData.pythonCurriculum.map((week) => (
                    <AccordionItem key={week.week} value={`week-${week.week}`} className="week-card-accordion">
                      <AccordionTrigger className="week-accordion-trigger">
                        <div className="week-header-accordion">
                          <span className="week-number">Week {week.week}</span>
                          <h4 className="week-title">{week.title}</h4>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="topic-list">
                          {week.topics.map((topic, idx) => (
                            <li key={idx} className="topic-item">
                              <CheckCircle size={16} className="check-icon" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </motion.div>

            {/* AI Track */}
            <motion.div
              className="curriculum-track"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.2 }}
            >
              <div className="track-header">
                <Rocket size={32} className="track-icon" />
                <h3 className="track-title">AI & Agentic Systems</h3>
                <p className="track-duration">Weeks 7-12</p>
              </div>
              <div className="track-content">
                <Accordion type="single" collapsible className="week-accordion">
                  {courseData.aiCurriculum.map((week) => (
                    <AccordionItem key={week.week} value={`week-${week.week}`} className="week-card-accordion">
                      <AccordionTrigger className="week-accordion-trigger">
                        <div className="week-header-accordion">
                          <span className="week-number">Week {week.week}</span>
                          <h4 className="week-title">{week.title}</h4>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="topic-list">
                          {week.topics.map((topic, idx) => (
                            <li key={idx} className="topic-item">
                              <CheckCircle size={16} className="check-icon" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="section highlights-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">Key Highlights</h2>
            <p className="section-subtitle">Why choose our program?</p>
          </motion.div>

          <div className="highlights-grid">
            {courseData.highlights.map((highlight, idx) => {
              const Icon = iconMap[highlight.icon];
              return (
                <motion.div
                  key={idx}
                  className="highlight-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: idx * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <div className="highlight-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="highlight-title">{highlight.title}</h3>
                  <p className="highlight-description">{highlight.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mentorship Section */}
      <section id="mentorship" className="section mentorship-section">
        <div className="container">
          <div className="mentorship-content">
            <motion.div
              className="mentorship-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="section-title">{courseData.mentorshipDetails.title}</h2>
              <p className="section-description">{courseData.mentorshipDetails.description}</p>
              <ul className="feature-list">
                {courseData.mentorshipDetails.features.map((feature, idx) => (
                  <li key={idx} className="feature-item">
                    <CheckCircle size={20} className="feature-icon" />
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="mentorship-visual"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="mentor-grid">
                <div className="mentor-placeholder">
                  <Users size={48} />
                  <p>Industry Experts</p>
                </div>
                <div className="mentor-placeholder">
                  <Target size={48} />
                  <p>Personalized Guidance</p>
                </div>
                <div className="mentor-placeholder">
                  <BookOpen size={48} />
                  <p>Weekly Sessions</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Internship Section */}
      <section id="internship" className="section internship-section">
        <div className="container">
          <div className="internship-content">
            <motion.div
              className="internship-visual"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="internship-icon-large">
                <Briefcase size={80} />
              </div>
            </motion.div>
            <motion.div
              className="internship-text"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="section-title">{courseData.internshipDetails.title}</h2>
              <p className="section-description">{courseData.internshipDetails.description}</p>
              <ul className="feature-list">
                {courseData.internshipDetails.benefits.map((benefit, idx) => (
                  <li key={idx} className="feature-item">
                    <CheckCircle size={20} className="feature-icon" />
                    {benefit}
                  </li>
                ))}
              </ul>
              <div className='btn-container'>
                <button className="btn-primary" onClick={handleEnrollClick}>
                Apply for Internship
              </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="gradient-waitlist">
          <div className="container-narrow">
            <motion.div
              className="cta-content"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="cta-title">Ready to Start Your AI Journey?</h2>
              <p className="cta-description">
                Join hundreds of students building their future in AI and technology.
                Limited seats available!
              </p>
              <button className="btn-primary btn-large" onClick={handleEnrollClick}>
                Register Now
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img 
                  src={logo} 
                  alt="Inkube Solutions" 
                  className="footer-logo-image"
                />
              </div>
              <p className="footer-tagline">Building the next generation of AI professionals</p>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">Contact</h4>
              <div className="footer-links">
                <a href="mailto:info@inkubetech.com" className="footer-link">
                  <Mail size={18} />
                  info@inkubetech.com
                </a>
                <a href="tel:+919999999999" className="footer-link">
                  <Phone size={18} />
                  +91 99726 14469
                </a>
              </div>
            </div>
            <div className="footer-section">
              <h4 className="footer-title">Quick Links</h4>
              <div className="footer-links">
                <a href="#about" className="footer-link">About Program</a>
                <a href="#curriculum" className="footer-link">Curriculum</a>
                <a href="#mentorship" className="footer-link">Mentorship</a>
                <a href="#internship" className="footer-link">Internship</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Inkube Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
